import React from 'react';
import Icon from '../../../components/AppIcon';
import { parseLocalDate } from '../../../utils/dateUtils';

const getUrgency = (dueDate) => {
  const today = new Date();
  const due = parseLocalDate(dueDate);
  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-200', icon: 'AlertCircle', iconColor: 'text-red-500', days: diffDays };
  if (diffDays <= 3) return { label: 'Critical', color: 'bg-red-50 text-red-600 border-red-200', icon: 'AlertTriangle', iconColor: 'text-red-500', days: diffDays };
  if (diffDays <= 7) return { label: 'Urgent', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'Clock', iconColor: 'text-amber-500', days: diffDays };
  return { label: 'Upcoming', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: 'Calendar', iconColor: 'text-blue-500', days: diffDays };
};

const UpcomingDeliverables = ({ deliverables, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="animate-pulse space-y-3">
          {[1,2,3,4]?.map(i => <div key={i} className="h-14 bg-gray-100 rounded" />)}
        </div>
      </div>
    );
  }

  const sorted = [...(deliverables ?? [])]?.filter(d => d?.dueDate || d?.endDate)?.sort((a, b) => new Date(a?.dueDate ?? a?.endDate) - new Date(b?.dueDate ?? b?.endDate))?.slice(0, 8);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Icon name="Calendar" size={16} className="text-amber-500" />
          Upcoming Deliverables
        </h3>
        <span className="text-xs text-gray-400">{sorted?.length} item{sorted?.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="divide-y divide-gray-50">
        {sorted?.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-400 text-sm">
            No upcoming deliverables in the next 14 days
          </div>
        ) : (
          sorted?.map((item, idx) => {
            const dueDate = item?.dueDate ?? item?.endDate;
            const urgency = getUrgency(dueDate);
            return (
              <div key={item?.id ?? idx} className={`px-5 py-3 flex items-start gap-3 border-l-4 ${urgency?.color}`}>
                <Icon name={urgency?.icon} size={16} className={`mt-0.5 flex-shrink-0 ${urgency?.iconColor}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-gray-800 truncate">{item?.name ?? item?.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${urgency?.color}`}>
                      {urgency?.days < 0 ? `${Math.abs(urgency?.days)}d overdue` : urgency?.days === 0 ? 'Due today' : `${urgency?.days}d left`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-400">{item?.projectName ?? 'Project'}</span>
                    {item?.assignedResources?.length > 0 && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Icon name="Users" size={10} />
                        {item?.assignedResources?.length} resource{item?.assignedResources?.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">{parseLocalDate(dueDate)?.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default UpcomingDeliverables;
